// src/services/queries/championship.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { ChampionshipService } from '../instances/championship';
import { ChampionshipConverter } from '@/converters';
import { PostChampionshipRequest } from '../request/championship';

export const useChampionships = (params?: QueryParams) => {
  const queryClient = useQueryClient();

  const queryKey = useMemo(() => ['GET_CHAMPIONSHIPS', params] as const, [params]);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await ChampionshipService.getChampionships(params);
      if (err) {
        throw err;
      }
      return {
        data: ChampionshipConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    refetchOnWindowFocus: false,
  });

  const forceRefetch = useCallback(() => {
    queryClient.removeQueries({ queryKey });
    queryClient.refetchQueries({ queryKey });
  }, [queryClient, queryKey]);

  return useMemo(() => ({ ...query, forceRefetch }), [query, forceRefetch]);
};

export const useChampionshipById = (id: string) => {
  const queryClient = useQueryClient();

  const queryKey = useMemo(() => ['GET_CHAMPIONSHIP_BY_ID', id] as const, [id]);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await ChampionshipService.getChampionshipById(id);
      if (err) {
        throw err;
      }
      return ChampionshipConverter.toDomainFromByIdResponse(data);
    },
    refetchOnWindowFocus: false,
    enabled: !!id,
  });

  const forceRefetch = useCallback(() => {
    queryClient.removeQueries({ queryKey });
    queryClient.refetchQueries({ queryKey });
  }, [queryClient, queryKey]);

  return useMemo(() => ({ ...query, forceRefetch }), [query, forceRefetch]);
};

export const usePostChampionshipMutation = () => {
  return useMutation({
    mutationKey: ['POST_CHAMPIONSHIP'],
    mutationFn: async (payload: PostChampionshipRequest) => {
      const [err, data] = await ChampionshipService.postChampionship(payload);
      if (err) {
        throw err;
      }
      return ChampionshipConverter.toDomainFromPostResponse(data);
    },
  });
};

export const usePutChampionshipMutation = () => {
  return useMutation({
    mutationKey: ['PUT_CHAMPIONSHIP'],
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: {
        // ou PutChampionshipRequest
        name: string;
        is_filtered: boolean;
        is_active: boolean;
      };
    }) => {
      const [err, data] = await ChampionshipService.putChampionship(id, payload);
      if (err) {
        throw err;
      }
      return ChampionshipConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeleteChampionshipMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_CHAMPIONSHIP'],
    mutationFn: async (id: string) => {
      const [err] = await ChampionshipService.deleteChampionship(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

